import { MouseEventHandler } from 'react';

import { css, cx } from '@emotion/css';
import { Chip, Theme, Typography } from '@mui/material';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons';
import { Tooltip } from '@/components/floating/Tooltip';
import { useStyles } from '@/hooks/useStyles';
import { PaletteColors } from '@/theme/PaletteOverrides.ts';
import { AtomProps, StyleClasses } from '@/uiKit/atoms/utils/AtomTypes.ts';

export type BaseChipSelectProps = {
  label: string;
  selected: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  tooltip?: string;
  disabled?: boolean;
  color?: PaletteColors;
};

export type ChipSelectClasses = StyleClasses<
  | 'chip'
  | 'selectedIcon'
  | 'tooltip'
  | 'tooltipText'
  | 'tooltipWrapper'
  | 'tooltipFloater'
>;

export type ChipSelectProps = AtomProps<BaseChipSelectProps, ChipSelectClasses>;

/**
 * ChipSelect is a functional component that renders a customizable Chip UI element.
 *
 * It allows for optional tooltip integration and styling.
 *
 * The Chip is used to select options like Type 1, Type 2 or Gestational diabetes
 */
export const ChipSelect = ({
  selected,
  tooltip,
  className,
  classes,
  ...props
}: ChipSelectProps) => {
  const styles = useStyles(makeStyles, selected);

  const chip = (
    <Chip
      icon={
        selected ? (
          <Icons.check className={cx(styles.icon, classes?.selectedIcon)} />
        ) : undefined
      }
      color="primary"
      variant="outlined"
      className={cx(styles.chip, className, classes?.chip)}
      {...props}
    />
  );

  if (tooltip) {
    return (
      <Tooltip
        className={classes?.tooltip}
        floaterClassName={classes?.tooltipFloater}
        wrapperClassName={classes?.tooltipWrapper}
        content={
          <Typography className={classes?.tooltipText}>{tooltip}</Typography>
        }
        placement="top"
      >
        <span>{chip}</span>
      </Tooltip>
    );
  }

  return chip;
};

const makeStyles = (theme: Theme, selected: boolean) => ({
  chip: css`
    padding-left: ${theme.spacing(6)};
    padding-right: ${theme.spacing(6)};
    background-color: ${selected
      ? theme.palette.background.default
      : theme.palette.common.white};
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
